import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CircularProgress, Modal } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  getResultsReportDownloadUrl,
  listResultsReportsForDatabase,
  ResultsReportJobStatus,
  ResultsReportOptions,
  ResultsReportRow,
  startResultsReport,
} from 'api/bitedb/resultsReport';
import {
  useResultsReportJob,
  UseResultsReportJobResult,
} from 'hooks/useResultsReportJob';

interface DatabaseResultsReportModalProps {
  databaseId: string;
  databaseName: string;
  open: boolean;
  onClose: () => void;
  /** When set the report is scoped to this single entity and only a PDF is generated. */
  entityId?: string;
  entityName?: string;
}

type Mode = 'config' | 'running' | 'done' | 'error';
type ReportArtifact = 'csv' | 'pdf';
type ReportJobFallbackMap = Record<string, ResultsReportRow>;

const formatBytes = (bytes?: number): string => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) {
    return '';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const closeIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
  >
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='#85888E'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const DatabaseResultsReportModal: React.FC<DatabaseResultsReportModalProps> = ({
  databaseId,
  databaseName,
  open,
  onClose,
  entityId,
  entityName,
}) => {
  const isEntityReport = Boolean(entityId);
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id || '';

  const [mode, setMode] = useState<Mode>('config');
  const [csvJobId, setCsvJobId] = useState<string | null>(null);
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isStarting, setIsStarting] = useState<boolean>(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [isFetchingDownload, setIsFetchingDownload] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [fallbackRowsByJobId, setFallbackRowsByJobId] =
    useState<ReportJobFallbackMap>({});

  const shouldTrackJobs = mode === 'running' || mode === 'done' || mode === 'error';
  const csvJob = useResultsReportJob(shouldTrackJobs ? csvJobId : null);
  const pdfJob = useResultsReportJob(shouldTrackJobs ? pdfJobId : null);

  useEffect(() => {
    if (
      mode !== 'running' ||
      !databaseId ||
      !groupId ||
      (!csvJobId && !pdfJobId)
    ) {
      return;
    }

    let isActive = true;
    const trackedJobIds = [csvJobId, pdfJobId].filter(
      (jobId): jobId is string => Boolean(jobId),
    );

    const pollRows = async () => {
      try {
        const response = await listResultsReportsForDatabase(databaseId, groupId, {
          page: 1,
          limit: 100,
        });
        if (!isActive) {
          return;
        }

        const matchedRows = response.data.filter(
          (row) => row.jobId && trackedJobIds.includes(row.jobId),
        );
        if (matchedRows.length === 0) {
          return;
        }

        setFallbackRowsByJobId((current) => {
          const next = { ...current };
          matchedRows.forEach((row) => {
            next[row.jobId] = row;
          });
          return next;
        });
      } catch {
        /* DB fallback is best effort; live status polling remains primary. */
      }
    };

    pollRows();
    const intervalId = window.setInterval(pollRows, 3000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [csvJobId, databaseId, groupId, mode, pdfJobId]);

  const resolveTerminalStatus = useCallback(
    (
      liveStatus: UseResultsReportJobResult['status'],
      fallbackRow?: ResultsReportRow,
    ): ResultsReportJobStatus | null => {
      if (fallbackRow?.status === 'completed' || fallbackRow?.status === 'failed') {
        return fallbackRow.status;
      }
      return liveStatus;
    },
    [],
  );

  const resolveTerminalStep = useCallback(
    (
      liveStep: UseResultsReportJobResult['step'],
      fallbackRow?: ResultsReportRow,
    ) => {
      if (fallbackRow?.status === 'completed') {
        return 'completed' as const;
      }
      return liveStep;
    },
    [],
  );

  const resolveTerminalResult = useCallback(
    (
      liveResult: UseResultsReportJobResult['result'],
      fallbackRow?: ResultsReportRow,
    ) => {
      if (liveResult) {
        return liveResult;
      }
      if (fallbackRow?.status !== 'completed') {
        return null;
      }
      return {
        s3Key: '',
        fileName: fallbackRow.fileName || '',
        entityCount: fallbackRow.entityCount || 0,
        fileSizeBytes: fallbackRow.fileSizeBytes || 0,
        generatedAt: fallbackRow.generatedAt || new Date().toISOString(),
      };
    },
    [],
  );

  const resolveJob = useCallback(
    (
      liveJob: UseResultsReportJobResult,
      jobId: string | null,
    ): UseResultsReportJobResult => {
      const fallbackRow = jobId ? fallbackRowsByJobId[jobId] : undefined;
      const status = resolveTerminalStatus(liveJob.status, fallbackRow);
      const entityCount =
        typeof fallbackRow?.entityCount === 'number'
          ? fallbackRow.entityCount
          : null;

      return {
        ...liveJob,
        status: status,
        step: resolveTerminalStep(liveJob.step, fallbackRow),
        processedEntities: liveJob.processedEntities ?? entityCount,
        totalEntities: liveJob.totalEntities ?? entityCount,
        percent:
          typeof liveJob.percent === 'number'
            ? liveJob.percent
            : status === 'completed'
              ? 100
              : null,
        error:
          status === 'failed'
            ? liveJob.error || fallbackRow?.errorMessage || null
            : null,
        result: resolveTerminalResult(liveJob.result, fallbackRow),
      };
    },
    [
      fallbackRowsByJobId,
      resolveTerminalResult,
      resolveTerminalStatus,
      resolveTerminalStep,
    ],
  );

  const resolvedCsvJob = resolveJob(csvJob, csvJobId);
  const resolvedPdfJob = resolveJob(pdfJob, pdfJobId);

  useEffect(() => {
    if (mode !== 'running') return;
    const csvFailed = resolvedCsvJob.status === 'failed';
    const pdfFailed = resolvedPdfJob.status === 'failed';
    if (csvFailed || pdfFailed) {
      setMode('error');
      return;
    }
    // PDF-only (entity report): done when just the PDF job completes
    if (isEntityReport && pdfJobId && resolvedPdfJob.status === 'completed') {
      setMode('done');
      return;
    }
    // Database report: both jobs must complete
    if (
      csvJobId &&
      pdfJobId &&
      resolvedCsvJob.status === 'completed' &&
      resolvedPdfJob.status === 'completed'
    ) {
      setMode('done');
    }
  }, [
    csvJobId,
    mode,
    pdfJobId,
    resolvedCsvJob.status,
    resolvedPdfJob.status,
  ]);

  const resetForm = useCallback(() => {
    setFileName('');
    setStartError(null);
    setDownloadError(null);
  }, []);

  useEffect(() => {
    if (open) {
      return;
    }
    // Always clear transient UI state on close
    setIsStarting(false);
    setIsFetchingDownload(false);
    setStartError(null);
    setDownloadError(null);
    // Preserve job IDs and mode when a report is still generating so that
    // reopening the modal can resume tracking and downloading the in-progress job.
    if (mode !== 'running') {
      setMode('config');
      setCsvJobId(null);
      setPdfJobId(null);
      setFileName('');
      setFallbackRowsByJobId({});
    }
  }, [open, mode]);

  const defaultFileName = isEntityReport
    ? `${entityName || 'Entity'} Report`
    : `${databaseName || 'Database'} Results Report`;

  const baseFileName = useMemo(() => {
    const rawName = fileName.trim() || defaultFileName;
    return rawName.replace(/\.(pdf|csv)$/i, '');
  }, [defaultFileName, fileName]);

  const buildOptions = useCallback(
    (artifact: ReportArtifact): ResultsReportOptions => {
      const entityFilters = entityId ? { entityIds: [entityId] } : undefined;

      if (artifact === 'csv') {
        return {
          reportType: 'raw_data',
          includeExecutiveSummary: false,
          includeScreeningResults: true,
          includeBusinessVerification: true,
          includeFullEvidence: true,
          topNResultsPerEntity: 0,
          entityFilters: entityFilters,
          output: {
            format: 'csv',
            paperSize: 'LETTER',
            orientation: 'landscape',
          },
          fileName: `${baseFileName} Detailed Results.csv`,
        };
      }

      return {
        // 'summary' never renders the per-entity detail page (see
        // resultsReport.service.ts's isSummaryReport branch) -- it only
        // produces the whole-database dashboard + aggregate hit counts,
        // which for a single-entity report reads exactly like a (tiny)
        // database report instead of that entity's own details. 'full' with
        // entityFilters set is the same combination EntityResultsDownloadModal
        // already uses for a per-entity PDF, and is what actually calls
        // renderEntityPage.
        reportType: isEntityReport ? 'full' : 'summary',
        includeExecutiveSummary: false,
        includeScreeningResults: true,
        includeBusinessVerification: true,
        includeFullEvidence: false,
        topNResultsPerEntity: 0,
        entityFilters: entityFilters,
        output: {
          format: 'pdf',
          paperSize: 'LETTER',
          orientation: 'portrait',
        },
        fileName: `${baseFileName} Summary Report.pdf`,
      };
    },
    [baseFileName, entityId, isEntityReport],
  );

  const handleGenerate = useCallback(async () => {
    setStartError(null);
    if (!groupId) {
      setStartError(
        'No team selected. Choose a team before generating the report.',
      );
      return;
    }

    setIsStarting(true);
    try {
      if (isEntityReport) {
        // Entity-scoped reports are PDF-only
        const pdfResponse = await startResultsReport({
          databaseId: databaseId,
          groupId: groupId,
          options: buildOptions('pdf'),
        });
        setPdfJobId(pdfResponse.jobId);
      } else {
        const [csvResponse, pdfResponse] = await Promise.all([
          startResultsReport({
            databaseId: databaseId,
            groupId: groupId,
            options: buildOptions('csv'),
          }),
          startResultsReport({
            databaseId: databaseId,
            groupId: groupId,
            options: buildOptions('pdf'),
          }),
        ]);
        setCsvJobId(csvResponse.jobId);
        setPdfJobId(pdfResponse.jobId);
      }
      setMode('running');
    } catch (error: any) {
      setStartError(error?.message || 'Failed to start report generation.');
    } finally {
      setIsStarting(false);
    }
  }, [buildOptions, databaseId, groupId]);

  const handleRetry = useCallback(() => {
    setCsvJobId(null);
    setPdfJobId(null);
    setFallbackRowsByJobId({});
    setMode('config');
    setDownloadError(null);
    setStartError(null);
  }, []);

  const handleDownload = useCallback(async () => {
    const completedJobIds = [csvJobId, pdfJobId].filter((id): id is string =>
      Boolean(id),
    );
    if (completedJobIds.length === 0) return;
    if (!groupId) {
      setDownloadError('No team selected. Cannot fetch the download URLs.');
      return;
    }

    setDownloadError(null);
    setIsFetchingDownload(true);
    try {
      const downloads = await Promise.all(
        completedJobIds.map((jobId) => getResultsReportDownloadUrl(jobId, groupId)),
      );
      downloads.forEach(({ url, fileName: downloadFileName }) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        if (downloadFileName) {
          link.download = downloadFileName;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    } catch (error: any) {
      setDownloadError(error?.message || 'Failed to prepare the downloads.');
    } finally {
      setIsFetchingDownload(false);
    }
  }, [csvJobId, groupId, pdfJobId]);

  const title = useMemo(() => {
    switch (mode) {
      case 'running':
        return isEntityReport ? 'Preparing entity report' : 'Preparing results report';
      case 'done':
        return isEntityReport ? 'Entity report ready' : 'Results report ready';
      case 'error':
        return 'Report generation failed';
      default:
        return isEntityReport ? 'Download Entity Report' : 'Download Results Report';
    }
  }, [isEntityReport, mode]);

  const subtitle = useMemo(() => {
    switch (mode) {
      case 'running':
        return isEntityReport
          ? 'The PDF is being generated in the background. You can close this modal and come back later.'
          : 'The CSV and PDF are being generated in the background. You can close this modal and come back later.';
      case 'done':
        return isEntityReport
          ? 'The summary PDF report is ready to open.'
          : 'Your detailed CSV and summary PDF are ready to open.';
      case 'error':
        return 'The report job did not complete successfully. You can retry with the same settings.';
      default:
        return isEntityReport
          ? `Generate a summary PDF report for ${entityName || 'this entity'}.`
          : `Create an enriched CSV export and summary PDF for ${databaseName}.`;
    }
  }, [databaseName, entityName, isEntityReport, mode]);

  const renderConfigView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 16, paddingBottom: 0 }}>
      <div className='audit-add-modal-cont-body-frame'>
        <div className='audit-add-modal-cont-body-frame-form-stretch'>
          <div className='audit-add-modal-cont-body-frame-form-row'>
            <div className='audit-add-modal-cont-body-frame-form-row-input'>
              <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                File name
              </div>
              <input
                className='audit-add-modal-cont-body-frame-form-input'
                type='text'
                placeholder={defaultFileName}
                value={fileName}
                onChange={(event) => setFileName(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='audit-add-modal-cont-body-frame'>
        <div
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid var(--bg-secondary)',
            background: 'var(--bg-primary)',
            padding: '14px 16px',
          }}
        >
          <div
            style={{
              color: 'var(--text-primary)',
              fontFamily: 'Inter',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px',
            }}
          >
            {isEntityReport ? 'This will generate a PDF report' : 'This will generate two files'}
          </div>
          <div
            style={{
              color: 'var(--text-secondary)',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '20px',
              marginTop: 4,
            }}
          >
            {isEntityReport
              ? 'A PDF summary covering AI risk assessment, adverse media, screening hits, and entity details.'
              : 'A CSV with entity inputs, assigned risk, screening hits, and business verification checks, plus a PDF summary of risk levels and hit counts.'}
          </div>
        </div>
      </div>

      {startError && (
        <div className='audit-add-modal-cont-body-frame' style={{ paddingTop: 0 }}>
          <div
            style={{
              width: '100%',
              borderRadius: 12,
              border: '1px solid #FECACA',
              background: '#FEF2F2',
              padding: '12px 14px',
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            {startError}
          </div>
        </div>
      )}
    </div>
  );

  const renderJobProgress = (label: string, job: UseResultsReportJobResult) => {
    const isFailed = job.status === 'failed' || (job.status === null && Boolean(job.error));
    const isCompleted = job.status === 'completed';
    const progressValue = typeof job.percent === 'number' ? job.percent : undefined;
    const barColor = isFailed ? '#B42318' : isCompleted ? '#16a34a' : 'var(--action-color)';
    const stepLabel = job.step
      ? job.step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : isCompleted
        ? 'Complete'
        : job.status === 'pending'
          ? 'Waiting to start…'
          : 'Processing…';

    return (
      <div style={{ flex: '1 1 0', minWidth: 180, borderRadius: 12, border: `1px solid ${isFailed ? '#FECACA' : 'var(--bg-secondary)'}`, background: isFailed ? '#FEF2F2' : 'var(--bg-primary)', padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: isFailed ? '#B42318' : 'var(--text-primary)', lineHeight: '18px' }}>
          {label}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isFailed && !isCompleted && (
            <CircularProgress size={13} sx={{ color: 'var(--action-color)', flexShrink: 0 }} />
          )}
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: isFailed ? '#B42318' : 'var(--text-secondary)', lineHeight: '16px', textTransform: 'capitalize' }}>
            {job.error ?? stepLabel}
          </div>
        </div>
        <div style={{ width: '100%', height: 4, borderRadius: 999, background: isFailed ? '#FECACA' : 'var(--bg-secondary)', overflow: 'hidden' }}>
          <div style={{ width: `${progressValue ?? (isCompleted ? 100 : isFailed ? 100 : 20)}%`, height: '100%', borderRadius: 999, background: barColor, transition: 'width 0.3s ease' }} />
        </div>
        {typeof job.processedEntities === 'number' && typeof job.totalEntities === 'number' && job.totalEntities > 0 && (
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--text-secondary)', lineHeight: '16px' }}>
            {job.processedEntities} of {job.totalEntities} entities
          </div>
        )}
      </div>
    );
  };

  const renderRunningView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 12, alignItems: 'stretch' }}>
      <div style={{ display: 'flex', gap: 12, width: '100%', padding: '0 24px', boxSizing: 'border-box', flexWrap: 'wrap' }}>
        {!isEntityReport && renderJobProgress('CSV Export', resolvedCsvJob)}
        {renderJobProgress('PDF Report', resolvedPdfJob)}
      </div>
      <div style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--text-secondary)', lineHeight: '18px', padding: '0 24px', width: '100%', boxSizing: 'border-box' }}>
        Jobs keep running even if you close this modal.
      </div>
    </div>
  );

  const renderFileCard = (
    label: string,
    job: UseResultsReportJobResult,
    fallbackName: string,
  ) => (
    <div className='audit-add-modal-cont-body-frame'>
      <div
        style={{
          width: '100%',
          borderRadius: 12,
          border: '1px solid var(--bg-secondary)',
          background: 'var(--bg-primary)',
          padding: '16px',
        }}
      >
        <div
          style={{
            color: 'var(--text-primary)',
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
          }}
        >
          {label}
        </div>
        <div
          style={{
            color: 'var(--text-secondary)',
            fontFamily: 'Inter',
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '20px',
            marginTop: 4,
          }}
        >
          {job.result?.fileName || fallbackName}
        </div>
        <div
          style={{
            color: 'var(--text-secondary)',
            fontFamily: 'Inter',
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '20px',
            marginTop: 4,
          }}
        >
          {typeof job.result?.entityCount === 'number'
            ? `${job.result.entityCount} entities`
            : 'Entity count unavailable'}
          {job.result?.fileSizeBytes ? ` • ${formatBytes(job.result.fileSizeBytes)}` : ''}
        </div>
      </div>
    </div>
  );

  const renderDoneView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 16 }}>
      <div className='audit-add-modal-cont-body-frame'>
        <div
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid #B7E4C7',
            background: '#F0FDF4',
            padding: '16px',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ color: '#166534', fontSize: 22, marginTop: '2px' }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                color: 'var(--bg-primary)',
                fontFamily: 'Inter',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: '20px',
              }}
            >
              {isEntityReport ? 'Your report is ready' : 'Your report files are ready'}
            </div>
            <div
              style={{
                color: 'var(--bg-tertiary)',
                fontFamily: 'Inter',
                fontSize: 13,
                fontWeight: 400,
                lineHeight: '20px',
                marginTop: 4,
              }}
            >
              {isEntityReport
                ? 'Open the PDF in a new tab to review or share it.'
                : 'Open the CSV and PDF in new tabs to review or share them.'}
            </div>
          </div>
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider' />

      {!isEntityReport && renderFileCard(
        'Detailed CSV export',
        resolvedCsvJob,
        `${baseFileName} Detailed Results.csv`,
      )}
      {renderFileCard(
        'Summary PDF report',
        resolvedPdfJob,
        `${baseFileName} Summary Report.pdf`,
      )}

      {downloadError && (
        <div className='audit-add-modal-cont-body-frame'>
          <div
            style={{
              width: '100%',
              borderRadius: 12,
              border: '1px solid #FECACA',
              background: '#FEF2F2',
              padding: '12px 14px',
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            {downloadError}
          </div>
        </div>
      )}
    </div>
  );

  const renderErrorView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 16 }}>
      <div className='audit-add-modal-cont-body-frame'>
        <div
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid #FECACA',
            background: '#FEF2F2',
            padding: '16px',
          }}
        >
          <div
            style={{
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px',
            }}
          >
            Report generation failed
          </div>
          {[resolvedCsvJob, resolvedPdfJob]
            .filter((job) => job.error || job.status === 'failed')
            .map((job, index) => (
              <div
                key={`${job.status}-${index}`}
                style={{
                  color: '#B42318',
                  fontFamily: 'Inter',
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: '20px',
                  marginTop: 4,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {job.error || `Job ${index + 1} ended with status ${job.status}.`}
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  const renderFooter = () => {
    if (mode === 'config') {
      return (
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div
              className='audit-modal-cont-footer-content-btn-cancel'
              onClick={onClose}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Cancel
              </div>
            </div>
            <div
              className='audit-modal-cont-footer-content-btn-upload'
              onClick={isStarting ? undefined : handleGenerate}
              style={isStarting ? { opacity: 0.75, cursor: 'wait' } : undefined}
            >
              <div
                className='audit-modal-cont-footer-content-btn-text'
                style={{ color: '#FFF' }}
              >
                {isStarting && (
                  <CircularProgress
                    size={18}
                    sx={{ color: '#FFF', marginRight: '10px' }}
                  />
                )}
                {isStarting ? 'Starting...' : 'Generate Report Files'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === 'running') {
      return (
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div
              className='audit-modal-cont-footer-content-btn-cancel'
              onClick={onClose}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Close
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === 'done') {
      return (
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div
              className='audit-modal-cont-footer-content-btn-cancel'
              onClick={onClose}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Close
              </div>
            </div>
            <div
              className='audit-modal-cont-footer-content-btn-upload'
              onClick={isFetchingDownload ? undefined : handleDownload}
              style={
                isFetchingDownload
                  ? { opacity: 0.75, cursor: 'wait' }
                  : undefined
              }
            >
              <div
                className='audit-modal-cont-footer-content-btn-text'
                style={{ color: '#FFF' }}
              >
                {isFetchingDownload ? (
                  <CircularProgress
                    size={18}
                    sx={{ color: '#FFF', marginRight: '10px' }}
                  />
                ) : (
                  <DownloadOutlinedIcon
                    sx={{ fontSize: 18, marginRight: '8px' }}
                  />
                )}
                {isFetchingDownload ? 'Preparing...' : isEntityReport ? 'Download PDF' : 'Download Files'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='audit-modal-cont-footer'>
        <div className='audit-modal-cont-footer-content'>
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={onClose}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Close
            </div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={() => {
              resetForm();
              handleRetry();
            }}
          >
            <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF' }}>
              Retry
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ outline: 'none' }}>
        <div
          className='audit-add-modal-cont'
          style={{
            width: 560,
            maxWidth: 'calc(100vw - 32px)',
            alignItems: 'stretch',
          }}
        >
          <div className='audit-add-modal-cont-head'>
            <div className='audit-add-modal-head-cont-change'>
              <div className='audit-add-modal-head-cont-icon'>
                <PictureAsPdfOutlinedIcon
                  sx={{ color: '#4895EF', width: '24px', height: '24px' }}
                />
              </div>
              <div className='audit-add-modal-head-cont-supporting'>
                <h1>{title}</h1>
                <p>{subtitle}</p>
              </div>
            </div>
            <div className='audit-add-modal-head-close' onClick={onClose}>
              {closeIcon}
            </div>
          </div>

          <div className='activity-cont-frame-left-table-feed-row-cont-divider' />

          {mode === 'config' && renderConfigView()}
          {mode === 'running' && renderRunningView()}
          {mode === 'done' && renderDoneView()}
          {mode === 'error' && renderErrorView()}

          {renderFooter()}
        </div>
      </div>
    </Modal>
  );
};

export default DatabaseResultsReportModal;
